import { useMemo, useState } from "react";
import clsx from "clsx";

import { pluralise } from "../lib/format";
import type { CollectionDetail, Quarry } from "../lib/types";
import { EmptyState } from "./EmptyState";
import { QuarryRow } from "./QuarryRow";
import { QuarryRowSkeleton } from "./Skeleton";

export type QuarrySort = "hunt" | "rarest" | "name";

export type QuarryListProps = {
  collection: CollectionDetail;
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
};

const SORTS: { value: QuarrySort; label: string }[] = [
  { value: "hunt", label: "Still to find" },
  { value: "rarest", label: "Rarest first" },
  { value: "name", label: "A–Z" },
];

function sortQuarry(quarry: readonly Quarry[], sort: QuarrySort): Quarry[] {
  const items = [...quarry];
  switch (sort) {
    case "rarest":
      return items.sort(
        (a, b) => b.rarity - a.rarity || Number(a.isFound) - Number(b.isFound) || a.name.localeCompare(b.name),
      );
    case "name":
      return items.sort((a, b) => a.name.localeCompare(b.name));
    case "hunt":
    default:
      // Unfound first, rarest of those at the top: the actual hunting order.
      return items.sort(
        (a, b) => Number(a.isFound) - Number(b.isFound) || b.rarity - a.rarity || a.name.localeCompare(b.name),
      );
  }
}

/**
 * The hunt list: a checklist of things worth finding in this place, plus the
 * ones you found that nobody suggested.
 */
export function QuarryList({ collection, onRefresh, refreshing = false, className }: QuarryListProps) {
  const [sort, setSort] = useState<QuarrySort>("hunt");

  const { suggested, bonuses } = useMemo(() => {
    const sorted = sortQuarry(collection.quarry, sort);
    return {
      suggested: sorted.filter((quarry) => !quarry.isBonus),
      bonuses: sorted.filter((quarry) => quarry.isBonus),
    };
  }, [collection.quarry, sort]);

  if (collection.quarryState === "pending") {
    return <QuarryPending className={className} />;
  }

  if (collection.quarryState === "failed") {
    return (
      <section className={clsx("sheet grain border-l-2 border-l-rust p-5", className)}>
        <p className="field-label text-rust">Hunt list</p>
        <h2 className="mt-2 display-5 text-xl leading-tight text-ink">
          The suggestions didn't come through.
        </h2>
        <p className="mt-2 max-w-prose font-prose leading-relaxed text-ink-soft">
          Your finds are all safely filed — it's only the list of things to look for that failed.
          Asking again usually sorts it.
        </p>
        {onRefresh ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="touch mt-4 border border-ink px-4 py-2 font-label text-sm tracking-wide text-ink transition-colors hover:bg-paper-deep disabled:opacity-60"
          >
            {refreshing ? "Asking again…" : "Draw up the list again"}
          </button>
        ) : null}
      </section>
    );
  }

  if (suggested.length === 0 && bonuses.length === 0) {
    return (
      <EmptyState title="Nothing on the list yet" motif="list" className={className}>
        <p>
          Foundling hasn't suggested anything for {collection.geoLabel} yet. Ask it to have a think,
          or just go and find something — the list fills itself in around your finds.
        </p>
        {onRefresh ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="link touch mt-4 font-label text-sm"
          >
            {refreshing ? "Asking…" : "Ask for suggestions"}
          </button>
        ) : null}
      </EmptyState>
    );
  }

  return (
    <div className={className}>
      <div className="mb-1 flex flex-wrap items-end justify-between gap-3 border-b-2 border-rule pb-2">
        <div>
          <h2 className="field-label text-ink">Things to look for</h2>
          <p className="mt-1 font-prose text-sm text-ink-soft">
            {pluralise(collection.quarryCount, "target")} · {collection.foundCount} ticked off
          </p>
        </div>

        <fieldset className="flex items-center gap-0">
          <legend className="sr-only">Sort the hunt list</legend>
          {SORTS.map((option) => {
            const active = sort === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                onClick={() => setSort(option.value)}
                className={clsx(
                  "border px-2.5 py-1.5 font-label text-[0.6875rem] tracking-widest uppercase transition-colors",
                  "-ml-px first:ml-0",
                  active
                    ? "border-ink bg-ink text-paper"
                    : "border-rule text-ink-soft hover:bg-paper-deep hover:text-ink",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </fieldset>
      </div>

      {suggested.length > 0 ? (
        <ul className="mb-2">
          {suggested.map((quarry) => (
            <QuarryRow key={quarry.id} quarry={quarry} />
          ))}
        </ul>
      ) : null}

      {bonuses.length > 0 ? (
        <section className="mt-8">
          <div className="border-b-2 border-ochre pb-2">
            <h2 className="field-label text-ochre">Bonus finds</h2>
            <p className="mt-1 max-w-prose font-prose text-sm text-ink-soft">
              Nobody suggested these. You found them anyway — which is rather the point.
            </p>
          </div>
          <ul>
            {bonuses.map((quarry) => (
              <QuarryRow key={quarry.id} quarry={quarry} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Claude is still writing the suggestions. */
function QuarryPending({ className }: { className?: string }) {
  return (
    <section className={clsx("sheet grain-strong relative p-5", className)} aria-busy="true">
      <p className="field-label text-ochre">Hunt list</p>
      <h2 className="mt-2 display-4 text-2xl leading-tight text-ink">
        Claude is drawing up your hunt list
      </h2>
      <p className="mt-2 max-w-prose font-prose leading-relaxed text-ink-soft">
        Working out what's worth finding around here, and where it hides. This takes a few seconds —
        the page will fill in on its own.
      </p>
      <p role="status" aria-live="polite" className="sr-only">
        Generating suggestions for this collection.
      </p>
      <ul className="mt-6">
        {[0, 1, 2, 3, 4].map((index) => (
          <QuarryRowSkeleton key={index} />
        ))}
      </ul>
    </section>
  );
}
